<template>
  <div class="post-attachments" :class="layoutClass">
    <div
      v-for="(attachment, index) in visibleAttachments"
      :key="attachment.id || index"
      class="attachment-item"
      @click="openLightbox(index)"
    >
      <!-- Image -->
      <img
        v-if="attachment.type === 'image'"
        :src="getAttachmentUrl(attachment)"
        :alt="attachment.original_name"
        class="attachment-image"
        loading="lazy"
      />

      <!-- YouTube Video -->
      <div v-else-if="attachment.type === 'video' && isYouTubeVideo(attachment)" class="attachment-video youtube-video">
        <iframe
          :src="getYouTubeEmbedUrl(attachment)"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
          @click.stop
        ></iframe>
        <div class="youtube-badge">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="#ff0000">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
        </div>
      </div>

      <!-- Regular Video -->
      <div v-else-if="attachment.type === 'video'" class="attachment-video">
        <video
          :src="getAttachmentUrl(attachment)"
          controls
          preload="metadata"
          @click.stop
        ></video>
      </div>

      <!-- Document -->
      <a
        v-else
        :href="getAttachmentUrl(attachment)"
        target="_blank"
        class="attachment-document"
        @click.stop
      >
        <div class="doc-icon">
          <svg viewBox="0 0 24 24" width="40" height="40" fill="#1877f2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/>
          </svg>
        </div>
        <div class="doc-info">
          <span class="doc-name">{{ attachment.original_name || attachment.file_name }}</span>
          <span class="doc-size">{{ formatFileSize(attachment.file_size) }}</span>
        </div>
      </a>

      <!-- Overlay for additional items -->
      <div
        v-if="index === maxVisible - 1 && hiddenCount > 0"
        class="more-overlay"
      >
        +{{ hiddenCount }}
      </div>
    </div>

    <!-- Lightbox Modal -->
    <div v-if="showLightbox" class="lightbox" @click="closeLightbox">
      <button class="lightbox-close" @click="closeLightbox">&times;</button>
      <button
        v-if="lightboxIndex > 0"
        class="lightbox-nav prev"
        @click.stop="prevImage"
      >
        <svg viewBox="0 0 24 24" width="32" height="32" fill="#fff">
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
        </svg>
      </button>
      <div class="lightbox-content" @click.stop>
        <img
          v-if="currentLightboxItem.type === 'image'"
          :src="getAttachmentUrl(currentLightboxItem)"
          :alt="currentLightboxItem.original_name"
        />
        <iframe
          v-else-if="currentLightboxItem.type === 'video' && isYouTubeVideo(currentLightboxItem)"
          :src="getYouTubeEmbedUrl(currentLightboxItem) + '&autoplay=1'"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
          class="youtube-lightbox"
        ></iframe>
        <video
          v-else-if="currentLightboxItem.type === 'video'"
          :src="getAttachmentUrl(currentLightboxItem)"
          controls
          autoplay
        ></video>
      </div>
      <button
        v-if="lightboxIndex < mediaAttachments.length - 1"
        class="lightbox-nav next"
        @click.stop="nextImage"
      >
        <svg viewBox="0 0 24 24" width="32" height="32" fill="#fff">
          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
        </svg>
      </button>
      <div class="lightbox-counter">
        {{ lightboxIndex + 1 }} / {{ mediaAttachments.length }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PostAttachments",
  props: {
    attachments: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      showLightbox: false,
      lightboxIndex: 0,
      maxVisible: 4,
    };
  },
  computed: {
    visibleAttachments() {
      return this.attachments.slice(0, this.maxVisible);
    },
    hiddenCount() {
      return Math.max(0, this.attachments.length - this.maxVisible);
    },
    mediaAttachments() {
      return this.attachments.filter(
        (a) => a.type === "image" || a.type === "video"
      );
    },
    currentLightboxItem() {
      return this.mediaAttachments[this.lightboxIndex] || {};
    },
    layoutClass() {
      const count = this.attachments.length;
      if (count === 1) return "layout-single";
      if (count === 2) return "layout-double";
      if (count === 3) return "layout-triple";
      return "layout-grid";
    },
  },
  methods: {
    isYouTubeVideo(attachment) {
      // Check if it's a YouTube video by mime_type or file_path
      if (attachment.mime_type === "video/youtube") return true;
      if (attachment.file_path?.includes("youtube.com/embed/")) return true;
      if (attachment.file_path?.includes("youtu.be/")) return true;
      return false;
    },

    getYouTubeEmbedUrl(attachment) {
      // Extract video ID
      let videoId = attachment.file_name;
      if (attachment.file_path?.includes("youtube.com/embed/")) {
        videoId = attachment.file_path.split("/embed/")[1]?.split("?")[0];
      } else if (!videoId) {
        videoId = attachment.file_path?.split("/").pop();
      }
      // Add parameters to hide related videos, branding, and channel info
      // rel=0 - no related videos from other channels
      // modestbranding=1 - minimal YouTube branding
      // showinfo=0 - hide title/uploader (deprecated but still helps)
      return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0`;
    },

    getAttachmentUrl(attachment) {
      if (!attachment.file_path) return "";
      if (attachment.file_path.startsWith("http")) return attachment.file_path;
      // Handle relative paths - chat-media is in the public folder (served by Vue dev server on 8080)
      if (attachment.file_path.startsWith("chat-media/")) {
        // In dev mode, serve from Vue dev server; in production, serve from root
        return "/" + attachment.file_path;
      }
      // Other files from backend
      const basePath = window.location.href.indexOf("8080") > -1
        ? "http://www.localhost:8888/"
        : "/";
      return basePath + attachment.file_path;
    },

    formatFileSize(bytes) {
      if (!bytes) return "";
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    },

    openLightbox(index) {
      const attachment = this.attachments[index];
      if (attachment.type !== "image" && attachment.type !== "video") return;

      // Find the index in media attachments
      const mediaIndex = this.mediaAttachments.findIndex(
        (a) => a.id === attachment.id || a.file_name === attachment.file_name
      );

      if (mediaIndex !== -1) {
        this.lightboxIndex = mediaIndex;
        this.showLightbox = true;
        document.body.style.overflow = "hidden";
      }
    },

    closeLightbox() {
      this.showLightbox = false;
      document.body.style.overflow = "";
    },

    prevImage() {
      if (this.lightboxIndex > 0) {
        this.lightboxIndex--;
      }
    },

    nextImage() {
      if (this.lightboxIndex < this.mediaAttachments.length - 1) {
        this.lightboxIndex++;
      }
    },
    // Stable handler reference so beforeUnmount can remove it. The previous
    // anonymous arrow function couldn't be unregistered, leaving a keydown
    // listener attached for every attachments component that ever mounted.
    handleLightboxKeydown(e) {
      if (!this.showLightbox) return;
      if (e.key === "Escape") this.closeLightbox();
      if (e.key === "ArrowLeft") this.prevImage();
      if (e.key === "ArrowRight") this.nextImage();
    },
  },
  mounted() {
    document.addEventListener("keydown", this.handleLightboxKeydown);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.handleLightboxKeydown);
  },
};
</script>

<style scoped>
.post-attachments {
  display: grid;
  gap: 2px;
  background: #000;
}

.layout-single {
  grid-template-columns: 1fr;
}

.layout-double {
  grid-template-columns: 1fr 1fr;
}

.layout-triple {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
}

.layout-triple .attachment-item:first-child {
  grid-row: span 2;
}

.layout-grid {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(2, 200px);
}

.attachment-item {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background: #f0f2f5;
}

.layout-single .attachment-item {
  max-height: 500px;
}

.layout-double .attachment-item,
.layout-triple .attachment-item,
.layout-grid .attachment-item {
  height: 200px;
}

.layout-triple .attachment-item:first-child {
  height: 100%;
}

.attachment-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s ease;
}

.attachment-item:hover .attachment-image {
  transform: scale(1.02);
}

.attachment-video {
  width: 100%;
  height: 100%;
}

.attachment-video video,
.attachment-video iframe {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.youtube-video {
  position: relative;
}

.youtube-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  padding: 4px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.attachment-document {
  display: flex;
  align-items: center;
  padding: 16px;
  background: #f0f2f5;
  height: 100%;
  text-decoration: none;
  gap: 12px;
}

.attachment-document:hover {
  background: #e4e6eb;
}

.doc-info {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.doc-name {
  font-size: 15px;
  font-weight: 500;
  color: #050505;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.doc-size {
  font-size: 13px;
  color: #65676b;
}

.more-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 600;
  color: #fff;
}

/* Lightbox */
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lightbox-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 24px;
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
}

.lightbox-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.lightbox-nav:hover {
  background: rgba(255, 255, 255, 0.2);
}

.lightbox-nav.prev {
  left: 16px;
}

.lightbox-nav.next {
  right: 16px;
}

.lightbox-content {
  max-width: 90vw;
  max-height: 90vh;
}

.lightbox-content img,
.lightbox-content video {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
}

.lightbox-content .youtube-lightbox {
  width: 80vw;
  height: 45vw;
  max-width: 1200px;
  max-height: 675px;
}

.lightbox-counter {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  font-size: 14px;
  background: rgba(0, 0, 0, 0.5);
  padding: 8px 16px;
  border-radius: 4px;
}

@media (max-width: 768px) {
  .layout-double .attachment-item,
  .layout-triple .attachment-item,
  .layout-grid .attachment-item {
    height: 150px;
  }

  .lightbox-nav {
    width: 36px;
    height: 36px;
  }

  .lightbox-nav.prev {
    left: 8px;
  }

  .lightbox-nav.next {
    right: 8px;
  }
}
</style>
